import { isMobile } from 'react-device-detect';
import { fabric } from 'fabric';
import { FabricTextBox } from '../../../global-types';
import { CanvasDataTypes, getGroupedItemByName } from '../../../utils/canvas-utils';
import { enforceMaxLength } from '../../../utils/enforce-max-length';
import { ensureHiddenTextareaMinFont } from '../../../utils/ensure-hidden-textarea-min-font';
import { togglePhotoTextZoneButtons, togglePhotoTextZoneResetButton } from '../../../utils/photo-text-zone-utils';

// import { getSpellcheckSuggestions, highlightMisspelledWords, setSuggestions } from '../spellcheck';

export type HandleTemporaryTextEditorProps = {
  currentCanvas: fabric.Canvas;
  zone?: fabric.Group | null;
};

/**
 * Creates a temporary input and then adds text to active photoTextZone on deselect.
 *
 * @param props - Props for temporary text editor
 * @example
 *   handleTemporaryTextEditor({ currentCanvas: canvas.current, zone });
 */
export const handleTemporaryTextEditor = ({ currentCanvas, zone = null }: HandleTemporaryTextEditorProps) => {
  // Get Current Zone, text item
  const photoTextZone = (zone || currentCanvas.getActiveObject()) as fabric.Group & FabricTextBox;

  const originalText = getGroupedItemByName(CanvasDataTypes.PhotoZoneTextbox, photoTextZone) as fabric.Textbox;
  if (currentCanvas && photoTextZone && originalText) {
    const { top, left, width, height } = photoTextZone;

    // Initialize temporary input over TAM photoZone.  Populate with original text.
    const temporaryInput = new fabric.Textbox(
      originalText.text as string,
      {
        textAlign: originalText.textAlign,
        fontSize: originalText.fontSize,
        fill: originalText.fill,
        fontWeight: 'normal',
        top: Math.ceil((top as number) - (height as number) / 2),
        isWrapping: true,
        left: Math.ceil((left as number) - (width as number) / 2),
        fontFamily: originalText.fontFamily,
        width,
        dirty: true,
        deltaY: 0,
        height,
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
        clipPath: originalText.clipPath,
        data: {
          testId: 'temporary-text',
          linkedZoneName: photoTextZone.name,
        },
        name: CanvasDataTypes.TemporaryTAMInput, //name can be used to prevent opening text drawer
        CanResizeTextArea: photoTextZone.CanResizeTextArea,
        CanEditFontSize: photoTextZone.CanEditFontSize,
        FontAutoSize: photoTextZone.FontAutoSize,
        CanEditFontColor: photoTextZone.CanEditFontColor,
      } as FabricTextBox,
    );

    // Hide original textbox and add Temporary text box to canvas.  Then, select the input and enter edit mode.
    originalText.set({ visible: false });
    currentCanvas.add(temporaryInput);

    currentCanvas.setActiveObject(temporaryInput);
    if (isMobile) {
      const handleEditingEntered = () => {
        temporaryInput.off('editing:entered', handleEditingEntered);
        ensureHiddenTextareaMinFont(temporaryInput.hiddenTextarea);
      };
      temporaryInput.on('editing:entered', handleEditingEntered);
    }
    temporaryInput.enterEditing();

    togglePhotoTextZoneResetButton(true, currentCanvas, photoTextZone);

    //handle needed actions after user exits editing the message
    temporaryInput.on('editing:exited', () => {
      //update the original photoTextZone's textbox with the newly added text and make it visible
      if (temporaryInput.text === '') {
        originalText.set({ text: temporaryInput.text });
        photoTextZone.data.hasContent = false;
        togglePhotoTextZoneButtons(true, currentCanvas, photoTextZone);
        togglePhotoTextZoneResetButton(false, currentCanvas, photoTextZone);
      } else if (temporaryInput.text) {
        originalText.set({
          textAlign: temporaryInput.textAlign,
          fontSize: temporaryInput.fontSize,
          fontFamily: temporaryInput.fontFamily,
          text: enforceMaxLength(temporaryInput.text, 3000),
          visible: true,
          height: temporaryInput.height,
          width: temporaryInput.width,
          fill: temporaryInput.fill,
        });

        // TODO: Implement spellcheck for S&S
        // Add isSpellcheck boolean param to handleTemporaryTextEditor func, with false as default value - isSpellcheck = false
        // Keep in mind that for S&S we'll need to also highlight the errors in temporary text when it is created
        // TemporaryInput.on('editing...') will need to be async = temporaryInput.on('editing:exited', async () => {
        /*
        if (isSpellcheckEnabled) {
          const spellcheckSuggestions = await getSpellcheckSuggestions(originalText.text as string);

          setSuggestions(originalText, spellcheckSuggestions);
          highlightMispelledWords(originalText,spellcheckSuggestions);
        }
        */

        photoTextZone.data.hasContent = true;
        photoTextZone.setOptions({ selectable: true });
        originalText.fire('changed');
      }

      //remove the temporary textbox
      currentCanvas.remove(temporaryInput);
      //bring back focus to photoTextZone
      currentCanvas.setActiveObject(photoTextZone);
      currentCanvas.renderAll();
    });
  }
};
